/**
 * ทีมออนไลน์: avatars with a green dot in the app header (a popover lists who is online and
 * what they have open), the "กำลังแก้ไขโดย …" chip for design cards, and the editor's own
 * "who else is here" strip. Data: app/presence.ts (heartbeat every ~30 s).
 */
import Users from 'lucide-react/dist/esm/icons/users.mjs';
import { roleLabel } from '../app/AppShell.js';
import { editingByOthers, initialsOf, usePresence } from '../app/presence.js';
import { href } from '../lib/router.js';
import { Popover } from './Popover.js';

const MAX_FACES = 4;

export function PresenceAvatars() {
  const p = usePresence();
  if (!p) return null;
  const others = p.online.filter((m) => !m.self);
  const total = p.online.length;
  const label = others.length ? `ทีมออนไลน์ ${total} คน` : 'ทีมออนไลน์: มีคุณคนเดียว';
  return (
    <Popover
      label={label}
      triggerClassName="xc-presence-btn"
      trigger={
        <span className="xc-presence-faces" aria-hidden="true">
          {others.length === 0 && (
            <span className="xc-presence-solo">
              <Users size={16} />
            </span>
          )}
          {others.slice(0, MAX_FACES).map((m) => (
            <span key={m.userId} className="xc-presence-face">
              {initialsOf(m.displayName)}
              <i className="xc-presence-dot" />
            </span>
          ))}
          {others.length > MAX_FACES && <span className="xc-presence-face is-more">+{others.length - MAX_FACES}</span>}
        </span>
      }
    >
      {() => (
        <div className="xc-menu-panel xc-presence-panel">
          <p className="xc-menu-title">ทีมออนไลน์ตอนนี้ · {total} คน</p>
          <ul className="xc-presence-list">
            {p.online.map((m) => (
              <li key={m.userId}>
                <span className="xc-presence-face is-lg" aria-hidden="true">
                  {initialsOf(m.displayName)}
                  <i className="xc-presence-dot" />
                </span>
                <span className="xc-presence-who">
                  <strong>
                    {m.displayName}
                    {m.self ? ' (คุณ)' : ''}
                  </strong>
                  <span className="xc-muted xc-small">
                    {roleLabel(m.role)}
                    {m.designs.length === 0 && ' · ออนไลน์'}
                  </span>
                  {m.designs.map((d) => (
                    <a key={d.id} href={href(`/d/${d.id}`)} className="xc-presence-doing">
                      {d.editing ? 'กำลังแก้ไข' : 'กำลังเปิดดู'} “{d.title}”
                    </a>
                  ))}
                </span>
              </li>
            ))}
          </ul>
          <p className="xc-muted xc-small">อัปเดตทุก 30 วินาที · แสดงเฉพาะงานที่คุณมีสิทธิ์เปิด</p>
        </div>
      )}
    </Popover>
  );
}

/** "กำลังแก้ไขโดย …" for a design card when another member holds its edit lease. */
export function EditingChip({ designId }: { designId: string }) {
  const p = usePresence();
  const who = editingByOthers(p).get(designId);
  if (!who) return null;
  return (
    <span className="xc-editing-chip" title={`${who} กำลังแก้ไขงานนี้อยู่`}>
      <i className="xc-presence-dot" aria-hidden="true" />
      กำลังแก้ไขโดย {who}
    </span>
  );
}

/** Editor header: others who have this design open, and who holds the edit lease. */
export function EditorPresence({ designId }: { designId: string }) {
  const p = usePresence();
  if (!p) return null;
  const here = p.online.filter((m) => !m.self && m.designs.some((d) => d.id === designId));
  const editor = editingByOthers(p).get(designId);
  if (!here.length && !editor) return null;
  const names = here.map((m) => m.displayName).join(', ');
  return (
    <span className="xc-editor-presence" role="status" aria-label={editor ? `กำลังแก้ไขโดย ${editor}${names ? ` · เปิดอยู่: ${names}` : ''}` : `เปิดงานนี้อยู่: ${names}`}>
      <span className="xc-presence-faces" aria-hidden="true">
        {here.slice(0, 3).map((m) => (
          <span key={m.userId} className="xc-presence-face" title={m.displayName}>
            {initialsOf(m.displayName)}
            <i className="xc-presence-dot" />
          </span>
        ))}
      </span>
      {editor && (
        <span className="xc-editor-presence-text" aria-hidden="true">
          กำลังแก้ไขโดย {editor}
        </span>
      )}
    </span>
  );
}
